/**
 * VilandeAktiebolagPage - Niche page for dormant companies
 *
 * SEO Target: /arsredovisning/vilande-aktiebolag
 * Primary keywords: årsredovisning vilande aktiebolag, vilande bolag årsredovisning
 */

import { useState } from "react";
import { PageMeta, getBreadcrumbSchema, SEO_CONFIG } from "@/lib/seo";
import { Breadcrumbs } from "@/layouts/PublicLayout";
import { Helmet } from "react-helmet-async";
import { AuthModal } from "@/components/auth/AuthModal";
import { usePricing } from "@/hooks/use-pricing";

const CheckIcon = () => (
  <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

const ArrowRightIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

export function VilandeAktiebolagPage() {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const pricing = usePricing();

  const breadcrumbItems = [
    { label: "Årsredovisning", href: "/arsredovisning" },
    { label: "Vilande Aktiebolag" },
  ];

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: "Hem", url: "/" },
    { name: "Årsredovisning", url: "/arsredovisning" },
    { name: "Vilande Aktiebolag", url: "/arsredovisning/vilande-aktiebolag" },
  ]);

  return (
    <>
      <PageMeta
        title="Årsredovisning för Vilande Aktiebolag | Enkelt & Billigt | Kvanten"
        description={`Skapa årsredovisning för vilande aktiebolag snabbt och enkelt. Även bolag utan verksamhet måste lämna årsredovisning. Från ${pricing.formattedPriceExclVat}.`}
        keywords="årsredovisning vilande aktiebolag, vilande bolag årsredovisning, vilande AB, inaktivt aktiebolag, bokslut vilande bolag"
        canonicalUrl={`${SEO_CONFIG.siteUrl}/arsredovisning/vilande-aktiebolag`}
        fullTitle
      />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-b from-amber-50 to-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={breadcrumbItems} />

          <div className="max-w-3xl">
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Årsredovisning för Vilande Aktiebolag
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Även ett vilande aktiebolag utan verksamhet måste lämna in årsredovisning till
              Bolagsverket varje år. Med Kvanten tar det bara några minuter - och du slipper
              dyra konsultarvoden.
            </p>

            <div className="flex flex-wrap gap-4 mb-8">
              <button
                onClick={() => setShowAuthModal(true)}
                className="inline-flex items-center px-6 py-3 text-lg font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors"
              >
                Skapa årsredovisning nu
                <ArrowRightIcon />
              </button>
            </div>

            <div className="bg-white rounded-lg p-4 border border-amber-200">
              <p className="text-amber-800 font-medium">
                OBS: Försenad årsredovisning kan leda till förseningsavgifter på 5 000-20 000 kr
                och risk för tvångslikvidation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What is vilande */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Vad är ett vilande aktiebolag?
              </h2>
              <p className="text-gray-600 mb-6">
                Ett vilande aktiebolag (även kallat "sovande bolag" eller "lagerbolag") är ett
                aktiebolag som inte bedriver någon aktiv verksamhet. Det kan bero på att:
              </p>
              <ul className="space-y-3">
                {[
                  "Verksamheten har avslutats men bolaget finns kvar",
                  "Bolaget väntar på rätt tillfälle att starta verksamhet",
                  "Bolaget används för att behålla ett företagsnamn",
                  "Bolaget har sålts som lagerbolag",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckIcon />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Måste vilande bolag göra årsredovisning?
              </h3>
              <p className="text-gray-600 mb-4">
                <strong>Ja!</strong> Alla aktiebolag i Sverige måste upprätta och lämna in
                årsredovisning till Bolagsverket, oavsett om de bedriver verksamhet eller inte.
              </p>
              <p className="text-gray-600 mb-4">
                Årsredovisningen ska lämnas in senast 7 månader efter räkenskapsårets slut.
                För bolag med kalenderår (1 jan - 31 dec) är deadline alltså 31 juli.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800">
                  <strong>Konsekvenser av utebliven årsredovisning:</strong>
                  <br />
                  - Förseningsavgift: 5 000 - 20 000 kr
                  <br />
                  - Risk för tvångslikvidation vid upprepade förseningar
                  <br />- Styrelseledamöter kan bli personligt ansvariga
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Simplified report */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Förenklad årsredovisning för vilande bolag
            </h2>
            <p className="text-xl text-gray-600">
              Ett vilande bolag har oftast minimal bokföring, vilket gör årsredovisningen enkel
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Typisk balansräkning</h3>
              <div className="space-y-4">
                <div className="border-b border-gray-200 pb-3">
                  <p className="font-semibold text-gray-900">Tillgångar</p>
                  <div className="flex justify-between text-gray-600">
                    <span>Kassa och bank</span>
                    <span>25 000 kr</span>
                  </div>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Eget kapital</p>
                  <div className="flex justify-between text-gray-600">
                    <span>Aktiekapital</span>
                    <span>25 000 kr</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Balanserat resultat</span>
                    <span>0 kr</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Årets resultat</span>
                    <span>0 kr</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Vad krävs?</h3>
              <ul className="space-y-3">
                {[
                  "Förvaltningsberättelse (kan vara kort)",
                  "Resultaträkning (ofta 0 kr)",
                  "Balansräkning (typiskt bara bankkonto + aktiekapital)",
                  "Några få noter",
                  "Underskrift av styrelsen",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckIcon />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-sm text-gray-500">
                OBS: Du behöver fortfarande en SIE-fil från bokföringsprogrammet, även om den
                bara innehåller aktiekapitalet.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why use Kvanten */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Varför använda Kvanten för vilande bolag?
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Snabbt och enkelt",
                description:
                  "Med minimal bokföring tar det bara 5-10 minuter att skapa årsredovisningen för ett vilande bolag.",
              },
              {
                title: "Billigare än konsult",
                description:
                  `En byrå tar ofta 2 000-5 000 kr för vilande bolag. Med Kvanten: ${pricing.formattedPriceExclVat} fast pris.`,
              },
              {
                title: "Regelrätt",
                description:
                  "Vi säkerställer att din årsredovisning uppfyller alla krav enligt K2 och ÅRL.",
              },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Vanliga frågor</h2>

          <div className="space-y-6">
            {[
              {
                q: "Kan jag göra årsredovisningen själv för ett vilande bolag?",
                a: "Ja! De flesta vilande aktiebolag har inte revisionsplikt och kan göra årsredovisningen själv. Kvanten guidar dig genom hela processen.",
              },
              {
                q: "Vad händer om jag missar deadline?",
                a: "Bolagsverket tar ut förseningsavgift: 5 000 kr första månaden, ytterligare 5 000 kr andra månaden, och 10 000 kr tredje månaden. Vid upprepade förseningar kan bolaget tvångslikvideras.",
              },
              {
                q: "Måste styrelsen underteckna?",
                a: "Ja, årsredovisningen måste undertecknas av samtliga styrelseledamöter. Med Kvanten kan ni signera digitalt med BankID.",
              },
              {
                q: "Behöver jag bokföringsprogram för ett vilande bolag?",
                a: "Tekniskt sett ja, du behöver exportera en SIE-fil. Men för ett helt vilande bolag kan den innehålla minimal data (bara aktiekapitalet).",
              },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.q}</h3>
                <p className="text-gray-600">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-24 bg-amber-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Få årsredovisningen klar på några minuter
          </h2>
          <p className="text-xl text-amber-100 mb-8">
            Slipp krångel och dyra konsulter. Skapa årsredovisning för ditt vilande bolag nu.
          </p>
          <button
            onClick={() => setShowAuthModal(true)}
            className="inline-flex items-center px-8 py-4 text-lg font-medium text-amber-600 bg-white rounded-lg hover:bg-amber-50 transition-colors"
          >
            Skapa årsredovisning
            <ArrowRightIcon />
          </button>
        </div>
      </section>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode="register"
      />
    </>
  );
}
